import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, Trash2, CreditCard, Truck, Loader2, Gift } from 'lucide-react';
import { useCart, unitPrice } from '../context/CartContext';
import { api, formatError, imgUrl } from '../lib/api';
import ShippingSelector from './ShippingSelector';
import { toast } from 'sonner';

const FREE_SHIPPING = 60;

const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const [loading, setLoading] = useState(false);
  const [shipping, setShipping] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [usePoints, setUsePoints] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('tf_token');
    return token ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true } : { withCredentials: true };
  };

  useEffect(() => {
    if (!open) return;
    api.get('/loyalty/me', authHeaders())
      .then((r) => setLoyalty(r.data))
      .catch(() => setLoyalty(null));
  }, [open]);

  const shippingFee = shipping ? (total >= FREE_SHIPPING ? 0 : shipping.method.price_cents / 100) : 0;
  const preDiscount = total + shippingFee;
  const canUsePoints = loyalty && loyalty.enabled && loyalty.points > 0;
  const pointsDiscount = (usePoints && canUsePoints)
    ? Math.min(loyalty.discount_value, Math.max(0, Math.round((preDiscount - 0.50) * 100) / 100))
    : 0;
  const grandTotal = Math.max(0, Math.round((preDiscount - pointsDiscount) * 100) / 100);
  const shippingReady = shipping && (shipping.method.mode !== 'PARCEL_POINT' || shipping.point);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty, variant: i.variant?.label || null })),
        origin_url: window.location.origin,
        shipping: shipping ? { method_id: shipping.method.id, relay_point: shipping.point } : undefined,
        use_points: usePoints && canUsePoints,
      }, authHeaders());
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e, 'Impossible de lancer le paiement'));
      setLoading(false);
    }
  };

  const remaining = Math.max(0, FREE_SHIPPING - total);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-void/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 z-[75] flex h-[100dvh] w-full max-w-md flex-col border-l border-neon/25 bg-panel"
            data-lenis-prevent
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-white/8 p-5">
              <h3 className="font-display text-lg font-bold tracking-tight text-slate-100">
                PANIER <span className="text-neon">//</span> INCUBATION
              </h3>
              <button onClick={() => setOpen(false)} data-testid="cart-drawer-close" className="flex h-9 w-9 items-center justify-center border border-white/15 text-slate-400 transition-colors hover:border-neon hover:text-neon">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain p-5" data-lenis-prevent>
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center" data-testid="cart-empty">
                  <Truck size={32} className="text-slate-600" />
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                    La chambre d'incubation est vide
                  </p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map(({ key, product, qty, variant }) => (
                    <motion.li
                      layout
                      key={key}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 30 }}
                      className="clip-frame-sm flex gap-4 border border-white/8 bg-void/60 p-3"
                      data-testid={`cart-item-${product.id}`}
                    >
                      <img src={imgUrl(product.image)} alt={product.name} className="clip-frame-sm h-20 w-20 object-cover" />
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold leading-tight text-slate-100">{product.name}</p>
                            {variant && (
                              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-cyber" data-testid={`cart-item-variant-${product.id}`}>
                                {variant.label}
                              </p>
                            )}
                          </div>
                          <button onClick={() => remove(key)} data-testid={`cart-remove-${product.id}`} className="shrink-0 text-slate-500 transition-colors hover:text-red-400">
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-white/10">
                            <button onClick={() => setQty(key, qty - 1)} data-testid={`cart-qty-minus-${product.id}`} className="px-2 py-1 text-slate-400 hover:text-neon"><Minus size={12} /></button>
                            <span className="w-8 text-center font-mono text-xs text-slate-200">{qty}</span>
                            <button onClick={() => setQty(key, qty + 1)} data-testid={`cart-qty-plus-${product.id}`} className="px-2 py-1 text-slate-400 hover:text-neon"><Plus size={12} /></button>
                          </div>
                          <span className="font-mono text-sm font-bold text-neon">
                            {(unitPrice({ product, variant }) * qty).toFixed(2).replace('.', ',')} €
                          </span>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}

              {items.length > 0 && (
              <div className="mt-5 border-t border-white/8 pt-5">
                <div className="mb-4">
                  <div className="mb-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <span>{remaining > 0 ? `Plus que ${remaining.toFixed(2).replace('.', ',')} € pour la livraison offerte` : 'Livraison offerte débloquée'}</span>
                    <span className="text-neon">{Math.min(100, (total / FREE_SHIPPING) * 100).toFixed(0)} %</span>
                  </div>
                  <div className="h-1 w-full bg-white/8">
                    <motion.div
                      className="h-full bg-neon"
                      animate={{ width: `${Math.min(100, (total / FREE_SHIPPING) * 100)}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <ShippingSelector subtotal={total} value={shipping} onChange={setShipping} />
                </div>
                {canUsePoints && (
                  <button
                    type="button"
                    onClick={() => setUsePoints((v) => !v)}
                    data-testid="use-points-toggle"
                    className={`clip-frame-sm mb-4 flex w-full items-center gap-3 border p-3 text-left transition-colors ${usePoints ? 'border-neon/60 bg-neon/10' : 'border-white/12 hover:border-neon/40'}`}
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center border ${usePoints ? 'border-neon bg-neon text-void' : 'border-neon/40 text-neon'}`}>
                      <Gift size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-slate-100">
                        Utiliser mes {loyalty.points} points
                      </span>
                      <span className="block font-mono text-[10px] text-slate-400">
                        {usePoints ? `Réduction appliquée −${pointsDiscount.toFixed(2).replace('.', ',')} €` : `Jusqu'à −${loyalty.discount_value.toFixed(2).replace('.', ',')} € sur cette commande`}
                      </span>
                    </span>
                    <span className={`h-5 w-5 shrink-0 rounded-sm border ${usePoints ? 'border-neon bg-neon' : 'border-white/25'}`} />
                  </button>
                )}
                <div className="mb-4 space-y-1.5 border-t border-white/8 pt-4">
                  <div className="flex items-center justify-between font-mono text-xs text-slate-400">
                    <span>Sous-total</span>
                    <span>{total.toFixed(2).replace('.', ',')} €</span>
                  </div>
                  <div className="flex items-center justify-between font-mono text-xs text-slate-400">
                    <span>Livraison {shipping ? `— ${shipping.method.carrier}` : ''}</span>
                    <span className={shippingFee === 0 && shipping ? 'text-neon' : ''} data-testid="shipping-fee-display">
                      {!shipping ? '—' : shippingFee === 0 ? 'Offerte' : `${shippingFee.toFixed(2).replace('.', ',')} €`}
                    </span>
                  </div>
                  {pointsDiscount > 0 && (
                    <div className="flex items-center justify-between font-mono text-xs text-neon" data-testid="points-discount-line">
                      <span>Points fidélité</span>
                      <span>−{pointsDiscount.toFixed(2).replace('.', ',')} €</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Total</span>
                    <span className="font-display text-2xl font-extrabold text-slate-100" data-testid="cart-total">
                      {grandTotal.toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                </div>
                <button
                  onClick={checkout}
                  disabled={loading || !shippingReady}
                  data-testid="checkout-stripe-button"
                  className="clip-frame-sm flex w-full items-center justify-center gap-2 bg-neon px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-void transition-colors hover:bg-cyber disabled:opacity-50"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {loading ? 'Connexion au labo…' : !shipping ? 'Choisir la livraison' : shipping.method.mode === 'PARCEL_POINT' && !shipping.point ? 'Choisir un point relais' : 'Paiement sécurisé Stripe'}
                </button>
                <p className="mt-3 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-slate-600">
                  Mode test · Carte 4242 4242 4242 4242
                </p>
              </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
